"use client";

import { useEffect } from "react";
import { applyPreferredLanguage } from "@/i18n/config";

// The first page of a browser session keeps the splash up briefly so it reads
// as an intro rather than a flicker; later loads drop it as soon as ready.
const SPLASH_SEEN_KEY = "elvet_splash_seen";
const FIRST_VISIT_MIN_MS = 900;

const hideSplash = () => {
  const splash = document.getElementById("brand-splash");
  if (!splash) return;
  let firstVisit = false;
  try {
    firstVisit = !sessionStorage.getItem(SPLASH_SEEN_KEY);
    sessionStorage.setItem(SPLASH_SEEN_KEY, "1");
  } catch {
    /* storage blocked: treat as a repeat visit */
  }
  const wait = firstVisit ? Math.max(0, FIRST_VISIT_MIN_MS - performance.now()) : 0;
  // Hidden, not removed: React owns this node from the server HTML.
  window.setTimeout(() => splash.classList.add("brand-splash--hidden"), wait);
};

/**
 * Side effects that used to live in `src/main.tsx`:
 * console noise filtering and revealing `#root` once fonts are ready.
 */
export function AppBootstrap() {
  useEffect(() => {
    // Suppress expected errors from browser console
    const originalError = console.error;
    console.error = (...args: any[]) => {
      const message = args[0]?.toString() || "";
      const fullMessage = args.map((a) => String(a)).join(" ");

      // Filter out source map errors from browser extensions
      if (
        message.includes("Source map error") ||
        message.includes("installHook.js.map") ||
        (message.includes("JSON.parse") && message.includes("source map"))
      ) {
        return; // Suppress these errors
      }

      // Filter out expected 404 errors for nurse profile resolution and task endpoints
      if (
        fullMessage.includes("404") &&
        (fullMessage.includes("nurses/me/") ||
          fullMessage.includes("nurses/by-user/") ||
          fullMessage.includes("tasks/to-do/by_id/") ||
          fullMessage.includes("tasks/done/by_id/") ||
          fullMessage.includes("tasks/done/today/by_id/"))
      ) {
        return; // Suppress expected 404s
      }

      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    const reveal = () => {
      requestAnimationFrame(() => {
        document.getElementById("root")?.classList.add("loaded");
      });
      hideSplash();
    };

    // Switch to the visitor's language before revealing, so a non-Russian
    // visitor doesn't see the server-rendered Russian text flash first.
    const language = applyPreferredLanguage();

    // Wait for CSS/fonts to load before revealing content
    if (document.fonts && document.fonts.ready) {
      Promise.all([document.fonts.ready, language.ready]).finally(reveal);
      return language.dispose;
    } else {
      const onLoad = () => language.ready.finally(reveal);
      window.addEventListener("load", onLoad);
      return () => {
        window.removeEventListener("load", onLoad);
        language.dispose();
      };
    }
  }, []);

  return null;
}
